import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lightbulb,
  X,
  CheckCircle,
  AlertTriangle,
  Info,
  XCircle,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { IAlertProps } from "Interfaces/interface.uiUtils";

const alertConfig = {
  default: {
    bg: "bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900",
    border: "border-slate-200 dark:border-slate-700",
    text: "text-slate-800 dark:text-slate-200",
    icon: Info,
    iconColor: "text-slate-600 dark:text-slate-400",
  },
  info: {
    bg: "bg-gradient-to-r from-blue-50 via-blue-50 to-indigo-50 dark:from-blue-900/20 dark:via-blue-900/20 dark:to-indigo-900/20",
    border: "border-blue-200 dark:border-blue-800",
    text: "text-blue-900 dark:text-blue-100",
    icon: Lightbulb,
    iconColor: "text-blue-600 dark:text-blue-400",
  },
  success: {
    bg: "bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 dark:from-green-900/20 dark:via-emerald-900/20 dark:to-teal-900/20",
    border: "border-green-200 dark:border-green-800",
    text: "text-green-900 dark:text-green-100",
    icon: CheckCircle,
    iconColor: "text-green-600 dark:text-green-400",
  },
  warning: {
    bg: "bg-gradient-to-r from-yellow-50 via-amber-50 to-orange-50 dark:from-yellow-900/20 dark:via-amber-900/20 dark:to-orange-900/20",
    border: "border-yellow-200 dark:border-yellow-800",
    text: "text-yellow-900 dark:text-yellow-100",
    icon: AlertTriangle,
    iconColor: "text-yellow-600 dark:text-yellow-500",
  },
  error: {
    bg: "bg-gradient-to-r from-red-50 via-rose-50 to-pink-50 dark:from-red-900/20 dark:via-rose-900/20 dark:to-pink-900/20",
    border: "border-red-200 dark:border-red-800",
    text: "text-red-900 dark:text-red-100",
    icon: XCircle,
    iconColor: "text-red-600 dark:text-red-400",
  },
};

export const Alert: React.FC<IAlertProps> = ({
  children = "Alert Text",
  className = "",
  titleClassName = "",
  actionButtonClassName = "",
  type = "default",
  dismissible = true,
  onDismiss,
  title,
  action,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const config = alertConfig[type];
  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.3, ease: "easeOut" as const }}
          className={`relative overflow-hidden rounded-2xl border-2 backdrop-blur-sm
            ${config.bg} ${config.border} ${config.text}
            shadow-lg hover:shadow-xl transition-all duration-300
            ${className}`}
        >
          {/* Background Animation */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
            <motion.div
              className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-white/10"
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" as const }}
            />
            <motion.div
              className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-white/5"
              animate={{ rotate: -360 }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" as const }}
            />
          </div>

          <div className="relative p-6">
            <div className="flex items-start gap-4">
              {/* Text Content */}
              <div className="flex-1 min-w-0">
                {title && (
                  <motion.h4
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className={`font-bold text-lg mb-2 flex items-center gap-2 ${titleClassName}`}
                  >
                    {title}
                    {type === "info" && (
                      <Sparkles className="w-4 h-4 text-yellow-500" />
                    )}
                  </motion.h4>
                )}

                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: title ? 0.3 : 0.2 }}
                  className="text-sm leading-relaxed flex items-center justify-start gap-3"
                >
                  {children}
                </motion.div>

                {/* Action */}
                {action && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mt-4"
                  >
                    <button
                      onClick={action.onClick}
                      className={`
                        inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium
                        transition-all duration-200 hover:scale-105 active:scale-95
                        ${
                          type === "info"
                            ? "bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg"
                            : ""
                        }
                        ${
                          type === "success"
                            ? "bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg"
                            : ""
                        }
                        ${
                          type === "warning"
                            ? "bg-yellow-600 hover:bg-yellow-700 text-white shadow-md hover:shadow-lg"
                            : ""
                        }
                        ${
                          type === "error"
                            ? "bg-red-600 hover:bg-red-700 text-white shadow-md hover:shadow-lg"
                            : ""
                        }
                        ${
                          type === "default"
                            ? "bg-slate-600 hover:bg-slate-700 text-white shadow-md hover:shadow-lg"
                            : ""
                        }
                        ${actionButtonClassName}
                      `}
                    >
                      <span>{action.label}</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </motion.div>
                )}
              </div>

              {/* Dismiss Button */}
              {dismissible && (
                <motion.button
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, ease: "easeOut" as const }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleDismiss}
                  className={`
                    p-1.5 rounded-lg transition-all duration-200
                    hover:bg-white/20 dark:hover:bg-black/20
                    absolute top-0 right-1
                    ${config.iconColor}
                  `}
                >
                  <X className="w-4 h-4" />
                </motion.button>
              )}
            </div>
          </div>

          {/* Auto Dismiss Progress (for success type) */}
          {type === "success" && (
            <motion.div
              className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-green-400 to-emerald-500"
              initial={{ width: "100%" }}
              animate={{ width: "0%" }}
              transition={{ duration: 5, ease: "linear" as const }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
